/**
 * Labels for an identifier whose display name couldn't be resolved.
 *
 * Never fall back to rendering the identifier itself. Name lookups miss for
 * ordinary reasons — a tenant-scoped store that hasn't loaded yet, a record
 * outside the page the API returned, a deleted record, a failed fetch — and a
 * bare Mongo ObjectId on screen reads as corrupted data to the person looking
 * at it. Reported from #support on 2026-07-29, where a team ObjectId rendered
 * where the team name belonged.
 *
 * Prefer widening the lookup (check every store that carries the name) over
 * accepting the fallback; these constants are the honest last resort.
 */
export const UNRESOLVED_NAME = 'Name unavailable';
export const RESOLVING_NAME = 'Loading…';

/**
 * @param name       the resolved name, if a lookup found one
 * @param isResolving whether a fetch that could still supply the name is in
 *                    flight — only then is "Loading…" truthful
 */
export const displayName = (name?: string | null, isResolving = false): string =>
    // Trimmed: a whitespace-only name is not a name, and would otherwise render
    // as a blank pill with no fallback text.
    name?.trim() || (isResolving ? RESOLVING_NAME : UNRESOLVED_NAME);
